/* Brainy Bounce: interactive 3D toy scene (Three.js r128).
   Bo plus orbiting squishy toys. Pick one, then press / hold to squeeze it.
   Every toy is modelled in code from 3D geometry and has its own deformation.
   These are playful interpretations of the real toys, not physical simulations.
   Falls back to the illustrated SVG Bo when WebGL is unavailable. */
(function () {
  'use strict';

  var PI = Math.PI;
  function clamp(x, a, b) { return x < a ? a : x > b ? b : x; }
  function smooth(a, b, x) { var t = clamp((x - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function Spring(k, c, x) { this.k = k; this.c = c; this.x = x || 0; this.v = 0; }
  Spring.prototype.step = function (target, dt) {
    var n = Math.max(1, Math.ceil(dt / 0.012)), h = dt / n;
    for (var i = 0; i < n; i++) { var a = this.k * (target - this.x) - this.c * this.v; this.v += a * h; this.x += this.v * h; }
    return this.x;
  };
  Spring.prototype.snap = function (x) { this.x = x; this.v = 0; };
  Spring.prototype.restAt = function (t) { return Math.abs(this.x - t) < 0.0015 && Math.abs(this.v) < 0.01; };

  function boot(stage) {
    if (stage.__bb3d || typeof THREE === 'undefined') return;
    var probe = document.createElement('canvas');
    if (!(probe.getContext('webgl') || probe.getContext('experimental-webgl'))) return;
    stage.__bb3d = true;
    try { build(stage); } catch (err) {
      // Something failed: keep the illustrated Bo and the shopping content.
      var c = stage.querySelector('canvas'); if (c) c.remove();
      stage.classList.remove('is-3d');
      if (window.console) console.warn('Brainy Bounce 3D scene disabled:', err);
    }
  }

  function build(stage) {
    var hero = stage.closest('[data-bb-hero]') || document;
    var ui = {
      panel: hero.querySelector('[data-bb-play]'),
      chips: hero.querySelector('[data-bb-chips]'),
      name: hero.querySelector('[data-bb-name]'),
      hint: hero.querySelector('[data-bb-hint]'),
      live: hero.querySelector('[data-bb-live]'),
      squish: hero.querySelector('[data-bb-squish]'),
      back: hero.querySelector('[data-bb-back]'),
      color: hero.querySelector('[data-bb-color]'),
      prev: hero.querySelector('[data-bb-prev]'),
      next: hero.querySelector('[data-bb-next]'),
      bubble: hero.querySelector('[data-bb-bubble]'),
      pad: stage.querySelector('[data-bb-pad]'),
      tip: stage.querySelector('[data-bb-tip]'),
      stars: stage.querySelector('[data-bb-stars]')
    };
    var mobile = window.matchMedia('(max-width: 749px)').matches;
    var W = stage.clientWidth || 400, H = stage.clientHeight || 400;

    // ---------- renderer ----------
    var renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'low-power' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, mobile ? 1.5 : 2));
    renderer.setSize(W, H);
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    renderer.domElement.className = 'bb-3d__canvas';
    renderer.domElement.setAttribute('aria-hidden', 'true');
    stage.insertBefore(renderer.domElement, stage.firstChild);

    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(34, W / H, 0.1, 100);
    var camBase = new THREE.Vector3(0, 0.9, mobile ? 11 : 9.8);
    var lookAt = new THREE.Vector3(0, 0.25, 0);

    // ---------- soft studio lighting + environment reflections ----------
    (function env() {
      var es = new THREE.Scene();
      var g = new THREE.SphereGeometry(10, 32, 16), cols = [], p = g.attributes.position;
      var top = new THREE.Color(0xf4e9ff), mid = new THREE.Color(0x6a3a8c), side = new THREE.Color(0x2a8f9a), bot = new THREE.Color(0x1b0830), c = new THREE.Color();
      for (var i = 0; i < p.count; i++) {
        var y = p.getY(i) / 10, x = p.getX(i) / 10;
        if (y > 0) c.copy(mid).lerp(top, y); else c.copy(mid).lerp(bot, -y);
        c.lerp(side, clamp(-x, 0, 1) * 0.45);
        cols.push(c.r, c.g, c.b);
      }
      g.setAttribute('color', new THREE.Float32BufferAttribute(cols, 3));
      es.add(new THREE.Mesh(g, new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.BackSide })));
      var panel = new THREE.Mesh(new THREE.PlaneGeometry(3.5, 1.6), new THREE.MeshBasicMaterial({ color: 0xffffff }));
      panel.position.set(3, 6, 5); panel.lookAt(0, 0, 0); es.add(panel);
      var pm = new THREE.PMREMGenerator(renderer);
      scene.environment = pm.fromScene(es, 0.04).texture;
      pm.dispose();
    })();
    scene.add(new THREE.HemisphereLight(0xfff0ff, 0x3a1250, 0.7));
    var key = new THREE.DirectionalLight(0xffffff, 1.15); key.position.set(3.5, 7, 6); scene.add(key);
    var rim = new THREE.PointLight(0x2fe0d6, 0.9, 20); rim.position.set(-4.5, 3, -3); scene.add(rim);
    var fill = new THREE.PointLight(0xff2e93, 0.55, 20); fill.position.set(4.5, -1, 3); scene.add(fill);

    // ---------- helpers ----------
    function lin(m) { if (m.__lin) return; m.__lin = true; if (m.color) m.color.convertSRGBToLinear(); if (m.emissive) m.emissive.convertSRGBToLinear(); }
    function setHexLin(c, hex) { c.setHex(hex).convertSRGBToLinear(); return c; }
    function gloss(color, o) {
      var m = new THREE.MeshPhysicalMaterial({ color: color, roughness: 0.28, metalness: 0, clearcoat: 1, clearcoatRoughness: 0.14 });
      if (o) for (var k in o) m[k] = o[k];
      return m;
    }
    function matte(color, rough) { return new THREE.MeshStandardMaterial({ color: color, roughness: rough == null ? 0.8 : rough, metalness: 0 }); }
    function canvasTex(size, draw, rep) {
      var c = document.createElement('canvas'); c.width = c.height = size;
      draw(c.getContext('2d'), size);
      var t = new THREE.CanvasTexture(c); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.encoding = THREE.sRGBEncoding;
      if (rep) t.repeat.set(rep[0], rep[1]);
      return t;
    }
    function speckle(base, dots, n) {
      return canvasTex(128, function (x, s) {
        x.fillStyle = base; x.fillRect(0, 0, s, s);
        for (var i = 0; i < n; i++) { x.fillStyle = dots[i % dots.length]; x.globalAlpha = 0.5 + Math.random() * 0.5; x.beginPath(); x.arc(Math.random() * s, Math.random() * s, 0.6 + Math.random() * 1.6, 0, 7); x.fill(); }
      }, [3, 2]);
    }
    var bumpTex = canvasTex(256, function (x, s) {
      x.fillStyle = '#808080'; x.fillRect(0, 0, s, s);
      for (var i = 0; i < 900; i++) { var r = 1 + Math.random() * 4; var gr = x.createRadialGradient(0, 0, 0, 0, 0, r); var v = Math.random() > 0.5 ? 255 : 0; gr.addColorStop(0, 'rgba(' + v + ',' + v + ',' + v + ',.55)'); gr.addColorStop(1, 'rgba(128,128,128,0)'); x.save(); x.translate(Math.random() * s, Math.random() * s); x.fillStyle = gr; x.fillRect(-r, -r, r * 2, r * 2); x.restore(); }
    }, [3, 2]);
    var shadowTex = canvasTex(128, function (x, s) {
      var g = x.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
      g.addColorStop(0, 'rgba(18,4,36,0.55)'); g.addColorStop(0.5, 'rgba(18,4,36,0.25)'); g.addColorStop(1, 'rgba(18,4,36,0)');
      x.fillStyle = g; x.fillRect(0, 0, s, s);
    });
    function blobShadow() {
      var m = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.MeshBasicMaterial({ map: shadowTex, transparent: true, depthWrite: false }));
      m.rotation.x = -PI / 2; m.renderOrder = -1; scene.add(m); return m;
    }
    function storeBase(geo) { geo.userData.base = Float32Array.from(geo.attributes.position.array); return geo; }
    var GROUND = -1.78;

    // =====================================================================
    //  BO
    // =====================================================================
    var BoM = {
      pink: gloss(0xff4fa8), fold: gloss(0xff9ccd, { roughness: 0.35 }), white: gloss(0xffffff, { roughness: 0.15 }),
      iris: gloss(0x18b9c9), pupil: matte(0x120622, 0.25), shine: new THREE.MeshBasicMaterial({ color: 0xffffff }),
      mouth: matte(0x4a0d36, 0.6), tongue: gloss(0xff7a8a), cheek: new THREE.MeshStandardMaterial({ color: 0xff8fc8, transparent: true, opacity: 0.55, roughness: 0.8 }),
      limb: gloss(0xe43b92), lime: gloss(0xb6f23a), sole: gloss(0xffffff, { roughness: 0.5 })
    };
    var bo = new THREE.Group(); scene.add(bo);
    var boSquish = new THREE.Group(); boSquish.position.y = -1.7; bo.add(boSquish);
    var boBody = new THREE.Group(); boBody.position.y = 1.7; boSquish.add(boBody);
    var R = 1.3;
    boBody.add(new THREE.Mesh(new THREE.SphereGeometry(R, mobile ? 40 : 56, mobile ? 30 : 40), BoM.pink));
    for (var f = 0; f < 5; f++) {
      var pts = [], lat = 0.5 + f * 0.17;
      for (var s = 0; s <= 16; s++) {
        var u = -1.1 + (s / 16) * 2.2, wig = Math.sin(s * 1.7 + f) * 0.07, phi = PI / 2 - (lat + wig), th = u * (1.25 - f * 0.12), rr = R * 1.005;
        pts.push(new THREE.Vector3(rr * Math.sin(phi) * Math.sin(th), rr * Math.cos(phi), rr * Math.sin(phi) * Math.cos(th) * 0.98 - 0.02));
      }
      var tube = new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 40, 0.05, 8, false), BoM.fold);
      tube.rotation.x = -0.25; boBody.add(tube);
    }
    var boEyes = new THREE.Group(); boBody.add(boEyes);
    var boPupils = [];
    [-1, 1].forEach(function (side) {
      var eye = new THREE.Group(); eye.position.set(side * 0.46, 0.18, R * 0.86);
      var w = new THREE.Mesh(new THREE.SphereGeometry(0.38, 28, 20), BoM.white); w.scale.set(1, 1.15, 0.62); eye.add(w);
      var look = new THREE.Group(); look.position.z = 0.2; eye.add(look);
      var iris = new THREE.Mesh(new THREE.SphereGeometry(0.21, 20, 14), BoM.iris); iris.scale.z = 0.5; look.add(iris);
      var pupil = new THREE.Mesh(new THREE.SphereGeometry(0.12, 16, 12), BoM.pupil); pupil.position.z = 0.06; pupil.scale.z = 0.5; look.add(pupil);
      var s1 = new THREE.Mesh(new THREE.SphereGeometry(0.055, 10, 8), BoM.shine); s1.position.set(0.07, 0.08, 0.13); look.add(s1);
      boPupils.push(look); boEyes.add(eye);
    });
    [-1, 1].forEach(function (side) {
      var c = new THREE.Mesh(new THREE.SphereGeometry(0.18, 14, 10), BoM.cheek);
      c.scale.set(1.3, 0.75, 0.3); c.position.set(side * 0.82, -0.28, R * 0.72); c.lookAt(side * 2.5, -0.6, 4); boBody.add(c);
    });
    var boSmile = new THREE.Mesh(new THREE.TorusGeometry(0.34, 0.065, 12, 36, PI), BoM.mouth);
    boSmile.rotation.z = PI; boSmile.position.set(0, -0.3, R * 0.93); boSmile.rotation.x = -0.25; boBody.add(boSmile);
    var boTongue = new THREE.Mesh(new THREE.SphereGeometry(0.13, 14, 10), BoM.tongue);
    boTongue.scale.set(1.2, 0.6, 0.5); boTongue.position.set(0, -0.6, R * 0.86); boBody.add(boTongue);
    function boArm(side) {
      var g = new THREE.Group(); g.position.set(side * 1.18, -0.15, 0.1);
      var a = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 0.55, 10), BoM.limb); a.position.set(side * 0.22, 0.12, 0); a.rotation.z = side * -0.9; g.add(a);
      var h = new THREE.Mesh(new THREE.SphereGeometry(0.2, 16, 12), BoM.pink); h.position.set(side * 0.46, 0.32, 0.05); g.add(h);
      boBody.add(g); return g;
    }
    var boArmL = boArm(-1), boArmR = boArm(1);
    [-1, 1].forEach(function (side) {
      var leg = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.11, 0.5, 10), BoM.limb); leg.position.set(side * 0.42, 0.28, 0.05); boSquish.add(leg);
      var shoe = new THREE.Mesh(new THREE.SphereGeometry(0.3, 20, 14), BoM.lime); shoe.scale.set(1.15, 0.62, 1.55); shoe.position.set(side * 0.45, 0.14, 0.22); boSquish.add(shoe);
      var sole = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.34, 0.07, 20), BoM.sole); sole.scale.set(1, 1, 1.4); sole.position.set(side * 0.45, 0.02, 0.22); boSquish.add(sole);
    });
    var boHit = new THREE.Mesh(new THREE.SphereGeometry(1.6, 8, 6), new THREE.MeshBasicMaterial({ visible: false }));
    bo.add(boHit);
    var boShadow = blobShadow();

    // =====================================================================
    //  TOYS — each returns { root, inner, update(s, aux, dt, t), ... }
    // =====================================================================

    // ---------- 1. Atomic NeeDoh: squish pushes out through a polygon cage ----------
    function makeAtomic() {
      var root = new THREE.Group(), body = new THREE.Group(); root.add(body);
      var t = (1 + Math.sqrt(5)) / 2;
      var V = [[-1, t, 0], [1, t, 0], [-1, -t, 0], [1, -t, 0], [0, -1, t], [0, 1, t], [0, -1, -t], [0, 1, -t], [t, 0, -1], [t, 0, 1], [-t, 0, -1], [-t, 0, 1]]
        .map(function (p) { return new THREE.Vector3(p[0], p[1], p[2]).normalize(); });
      var adj = V.map(function () { return []; }), md = 9;
      for (var i = 0; i < 12; i++) for (var j = i + 1; j < 12; j++) md = Math.min(md, V[i].distanceTo(V[j]));
      for (i = 0; i < 12; i++) for (j = 0; j < 12; j++) if (i !== j && V[i].distanceTo(V[j]) < md * 1.01) adj[i].push(j);
      function P(a, b) { return V[a].clone().multiplyScalar(2 / 3).add(V[b].clone().multiplyScalar(1 / 3)).normalize(); }
      var arcs = [];
      for (i = 0; i < 12; i++) adj[i].forEach(function (j2) {
        if (j2 > i) arcs.push([P(i, j2), P(j2, i)]);
        adj[i].forEach(function (k2) { if (k2 > j2 && adj[j2].indexOf(k2) >= 0) arcs.push([P(i, j2), P(i, k2)]); });
      });
      // inner squishy ball
      var seg = mobile ? 60 : 84;
      var geo = storeBase(new THREE.SphereGeometry(1, seg, Math.round(seg * 0.75)));
      var base = geo.userData.base, n = base.length / 3, hole = new Float32Array(n);
      var p = new THREE.Vector3(), nrm = new THREE.Vector3(), tmp = new THREE.Vector3();
      for (var vi = 0; vi < n; vi++) {
        p.set(base[vi * 3], base[vi * 3 + 1], base[vi * 3 + 2]).normalize();
        var best = 9;
        for (var a = 0; a < arcs.length; a++) {
          var A = arcs[a][0], B = arcs[a][1], d;
          nrm.crossVectors(A, B).normalize();
          var inside = tmp.crossVectors(A, p).dot(nrm) >= 0 && tmp.crossVectors(p, B).dot(nrm) >= 0;
          d = inside ? Math.asin(Math.abs(clamp(p.dot(nrm), -1, 1))) : Math.min(Math.acos(clamp(p.dot(A), -1, 1)), Math.acos(clamp(p.dot(B), -1, 1)));
          if (d < best) best = d;
        }
        hole[vi] = smooth(0.085, 0.36, best); // 0 under a bar, 1 in the middle of an opening
      }
      var innerMat = gloss(0xffe23d, { roughness: 0.22 });
      var inner = new THREE.Mesh(geo, innerMat); body.add(inner);
      // cage: rounded bars + joints (instanced)
      var cageMat = gloss(0x2f6fe0, { roughness: 0.5, clearcoat: 0.3 });
      var rb = 0.085, CR = 1.0;
      var bars = new THREE.InstancedMesh(new THREE.CylinderGeometry(rb, rb, 1, 8, 1), cageMat, arcs.length);
      var m = new THREE.Matrix4(), q = new THREE.Quaternion(), up = new THREE.Vector3(0, 1, 0), sc = new THREE.Vector3();
      arcs.forEach(function (ab, ii) {
        var A = ab[0].clone().multiplyScalar(CR), B = ab[1].clone().multiplyScalar(CR);
        var mid = A.clone().add(B).multiplyScalar(0.5).normalize().multiplyScalar(CR * 1.01), dir = B.clone().sub(A);
        q.setFromUnitVectors(up, dir.clone().normalize()); sc.set(1, dir.length(), 1);
        m.compose(mid, q, sc); bars.setMatrixAt(ii, m);
      });
      var verts = []; arcs.forEach(function (ab) { ab.forEach(function (v) { if (!verts.some(function (w) { return w.distanceTo(v) < 1e-3; })) verts.push(v); }); });
      var joints = new THREE.InstancedMesh(new THREE.SphereGeometry(rb * 1.12, 10, 8), cageMat, verts.length);
      verts.forEach(function (v, ii) { m.compose(v.clone().multiplyScalar(CR * 1.01), new THREE.Quaternion(), new THREE.Vector3(1, 1, 1)); joints.setMatrixAt(ii, m); });
      var cage = new THREE.Group(); cage.add(bars, joints); body.add(cage);
      var pos = geo.attributes.position.array;
      var colorways = [{ cage: 0x2f6fe0, inner: 0xffe23d }, { cage: 0xff3fa4, inner: 0x2f86ff }, { cage: 0xffd21f, inner: 0xff4fa8 }];
      return {
        root: root, bottom: 1.05, heroScale: 1.25, k: 130, c: 11,
        colorways: colorways,
        setColor: function (cw) { setHexLin(cageMat.color, cw.cage); setHexLin(innerMat.color, cw.inner); },
        update: function (s) {
          var sp = Math.max(s, -0.15);
          for (var i2 = 0; i2 < n; i2++) {
            var h = hole[i2];
            // rest: slight dome in each opening; squeeze: rounded bulges push out, squish under bars is pressed in
            var dome = Math.sqrt(Math.max(0, 1 - (1 - h) * (1 - h)));
            var r = 0.97 + 0.035 * dome + sp * (0.24 * dome - 0.06 * (1 - h));
            pos[i2 * 3] = base[i2 * 3] * r; pos[i2 * 3 + 1] = base[i2 * 3 + 1] * r; pos[i2 * 3 + 2] = base[i2 * 3 + 2] * r;
          }
          geo.attributes.position.needsUpdate = true; geo.computeVertexNormals();
          cage.scale.set(1 + 0.03 * sp, 1 - 0.05 * sp, 1 + 0.03 * sp); // subtle flex only
          body.scale.set(1 + 0.02 * sp, 1 - 0.04 * sp, 1 + 0.02 * sp);
        }
      };
    }

    // ---------- 2. Gumdrop: dense, translucent, flat base ----------
    function makeGumdrop() {
      var root = new THREE.Group(), pivot = new THREE.Group(); pivot.position.y = -0.68; root.add(pivot);
      var Hh = 1.35;
      var prof = [[0.001, 0], [0.64, 0], [0.75, 0.03], [0.8, 0.12], [0.81, 0.32], [0.79, 0.58], [0.73, 0.83], [0.62, 1.05], [0.46, 1.22], [0.26, 1.32], [0.001, 1.36]];
      var curve = new THREE.SplineCurve(prof.map(function (p) { return new THREE.Vector2(p[0], p[1]); }));
      var geo = storeBase(new THREE.LatheGeometry(curve.getPoints(mobile ? 20 : 28), mobile ? 32 : 44));
      var mat = new THREE.MeshPhysicalMaterial({ color: 0x1fb3ff, transparent: true, opacity: 0.9, roughness: 0.36, clearcoat: 0.8, clearcoatRoughness: 0.25, bumpMap: bumpTex, bumpScale: 0.035 });
      mat.emissive = new THREE.Color(0x3fc8ff).multiplyScalar(0.12);
      var mesh = new THREE.Mesh(geo, mat); pivot.add(mesh);
      var base = geo.userData.base, pos = geo.attributes.position.array, n = base.length / 3;
      var colorways = [{ c: 0x1fb3ff }, { c: 0xff3d9e }, { c: 0x9d4dff }, { c: 0xff8a1f }];
      return {
        root: root, bottom: 0.7, heroScale: 1.15, k: 62, c: 6.2,
        colorways: colorways,
        setColor: function (cw) { setHexLin(mat.color, cw.c); setHexLin(mat.emissive, cw.c).multiplyScalar(0.22); },
        update: function (s) {
          for (var i = 0; i < n; i++) {
            var x = base[i * 3], y = base[i * 3 + 1], z = base[i * 3 + 2];
            var hn = clamp(y / Hh, 0, 1);
            var widen = 1 + 0.04 * s + 0.2 * s * Math.pow(Math.sin(PI * hn), 0.9) * (1 - 0.35 * hn);
            pos[i * 3] = x * widen; pos[i * 3 + 1] = y * (1 - 0.3 * s); pos[i * 3 + 2] = z * widen; // base stays at y = 0
          }
          geo.attributes.position.needsUpdate = true; geo.computeVertexNormals();
        }
      };
    }

    // ---------- 3. Fuzz Ball: wavy ridges covered in attached nubs ----------
    function makeFuzz() {
      var root = new THREE.Group();
      function ridge(x, y, z) { var la = Math.asin(clamp(y, -1, 1)), lo = Math.atan2(z, x); return smooth(-0.1, 0.65, Math.sin(9 * la + 1.6 * Math.sin(3 * lo))); }
      var geo = storeBase(new THREE.SphereGeometry(1, mobile ? 36 : 48, mobile ? 26 : 36));
      var base = geo.userData.base, pos = geo.attributes.position.array, n = base.length / 3, rb = new Float32Array(n);
      for (var i = 0; i < n; i++) { var x = base[i * 3], y = base[i * 3 + 1], z = base[i * 3 + 2]; rb[i] = 0.9 + 0.08 * ridge(x, y, z); }
      var mat = matte(0x2fb8e6, 0.72); mat.roughness = 0.7;
      var bodyMesh = new THREE.Mesh(geo, mat); root.add(bodyMesh);
      var N = mobile ? 650 : 1400, ga = PI * (3 - Math.sqrt(5));
      var nubGeo = new THREE.CylinderGeometry(0.016, 0.024, 1, 5, 1); nubGeo.translate(0, 0.5, 0);
      var nubMat = matte(0x5fd0f0, 0.65);
      var nubs = new THREE.InstancedMesh(nubGeo, nubMat, N); nubs.frustumCulled = false; root.add(nubs);
      var nd = [];
      for (i = 0; i < N; i++) {
        var yy = 1 - (i / (N - 1)) * 2, rr = Math.sqrt(1 - yy * yy), th = ga * i;
        var dx = Math.cos(th) * rr, dz = Math.sin(th) * rr, rg = ridge(dx, yy, dz);
        nd.push({ x: dx, y: yy, z: dz, r: 0.9 + 0.08 * rg, len: 0.02 + 0.11 * rg + Math.random() * 0.012, la: Math.asin(yy), lo: Math.atan2(dz, dx) });
      }
      var M = new THREE.Matrix4(), Q = new THREE.Quaternion(), UP = new THREE.Vector3(0, 1, 0), Pv = new THREE.Vector3(), Nv = new THREE.Vector3(), Hv = new THREE.Vector3(), Sv = new THREE.Vector3();
      var colorways = [{ c: 0x2fb8e6, n: 0x5fd0f0 }, { c: 0xff9a2e, n: 0xffb45c }, { c: 0x7ee04a, n: 0x9ef06e }, { c: 0xff4fa8, n: 0xff7cc0 }, { c: 0xa879ff, n: 0xc19cff }];
      return {
        root: root, bottom: 1.15, heroScale: 1.2, k: 140, c: 10, lag: [55, 3.2],
        colorways: colorways,
        setColor: function (cw) { setHexLin(mat.color, cw.c); setHexLin(nubMat.color, cw.n); },
        update: function (s, aux, dt, t) {
          var a = 1 + 0.14 * s, b = 1 - 0.24 * s;
          for (var i3 = 0; i3 < n; i3++) {
            var r = rb[i3];
            pos[i3 * 3] = base[i3 * 3] * r * a; pos[i3 * 3 + 1] = base[i3 * 3 + 1] * r * b; pos[i3 * 3 + 2] = base[i3 * 3 + 2] * r * a;
          }
          geo.attributes.position.needsUpdate = true; geo.computeVertexNormals();
          var ripple = clamp(aux - s, -0.6, 0.6); // delayed settle after release
          for (var k = 0; k < N; k++) {
            var d = nd[k];
            Pv.set(d.x * d.r * a, d.y * d.r * b, d.z * d.r * a);
            Nv.set(d.x / a, d.y / b, d.z / a).normalize();
            Hv.set(d.x, 0, d.z); var hl = Hv.length(); if (hl > 1e-3) Hv.multiplyScalar(1 / hl);
            var tilt = 0.85 * Math.max(s, 0) * Math.abs(d.y) + 0.9 * ripple * Math.sin(d.lo * 3 + d.la * 8 + t * 14);
            Nv.addScaledVector(Hv, tilt).normalize();
            Q.setFromUnitVectors(UP, Nv);
            Sv.set(1, d.len * (1 - 0.3 * Math.max(s, 0) * Math.abs(d.y)), 1);
            M.compose(Pv, Q, Sv); nubs.setMatrixAt(k, M);
          }
          nubs.instanceMatrix.needsUpdate = true;
        }
      };
    }

    // ---------- 4. Squeeza pizza: soft cheese, firmer crust, attached toppings ----------
    function makeSqueeza() {
      var root = new THREE.Group(), body = new THREE.Group(); root.add(body);
      body.rotation.set(-0.3, 0, 0.14);
      var NU = mobile ? 16 : 22, NV = mobile ? 10 : 14, L = 2.0, WC = 1.0, T = 0.36, YC = 0.88;
      function basePt(u, v, side, out) {
        var hw = 0.07 + 0.93 * WC * Math.pow(1 - u, 0.95);
        var zf = Math.sqrt(Math.max(0, 1 - v * v)) * Math.sqrt(Math.max(0, 1 - Math.pow(u, 8)));
        out.set(v * hw, YC - L * u, side * (T / 2) * zf + (side > 0 ? 0.03 * zf : 0));
        return out;
      }
      var state = { s: 0, bend: 0 };
      function deform(u, out) {
        var s = state.s;
        out.z *= 1 - Math.max(s, -0.2) * 0.55 * (0.4 + 0.6 * u);       // softer toward the tip
        out.x *= 1 + 0.16 * s * (0.3 + 0.7 * u);
        var phi = -state.bend * 0.55 * u * u;                            // fold strongest near the tip
        var yr = out.y - YC, zr = out.z;
        out.y = YC + yr * Math.cos(phi) - zr * Math.sin(phi);
        out.z = yr * Math.sin(phi) + zr * Math.cos(phi);
        return out;
      }
      var posArr = [], idx = [], uv = [], cols = [];
      var cY = new THREE.Color(0xffd84a), cO = new THREE.Color(0xffb02e), cc = new THREE.Color(), tv = new THREE.Vector3();
      [1, -1].forEach(function (side) {
        for (var iu = 0; iu <= NU; iu++) for (var iv = 0; iv <= NV; iv++) {
          var u = iu / NU, v = -1 + 2 * iv / NV; basePt(u, v, side, tv);
          posArr.push(tv.x, tv.y, tv.z); uv.push(u, v, side);
          cc.copy(cY).lerp(cO, clamp(Math.pow(Math.abs(v), 3) * 0.8 + (side < 0 ? 0.5 : 0), 0, 1)).convertSRGBToLinear(); cols.push(cc.r, cc.g, cc.b);
        }
      });
      var per = (NU + 1) * (NV + 1);
      for (var sIdx = 0; sIdx < 2; sIdx++) for (var iu2 = 0; iu2 < NU; iu2++) for (var iv2 = 0; iv2 < NV; iv2++) {
        var o = sIdx * per, a = o + iu2 * (NV + 1) + iv2, b = a + NV + 1, c2 = a + 1, d2 = b + 1;
        if (sIdx === 0) idx.push(a, c2, b, c2, d2, b); else idx.push(a, b, c2, c2, b, d2);
      }
      var geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(posArr, 3));
      geo.setAttribute('color', new THREE.Float32BufferAttribute(cols, 3));
      geo.setIndex(idx); geo.computeVertexNormals();
      var cheese = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.82, side: THREE.DoubleSide }));
      body.add(cheese);
      // crust: thick rounded tube with capped ends
      var crustPts = []; for (var ci = 0; ci <= 12; ci++) { var cx = -1.08 + 2.16 * ci / 12; crustPts.push(new THREE.Vector3(cx, YC + 0.05 + 0.06 * (1 - cx * cx), 0)); }
      var crustGeo = storeBase(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(crustPts), 28, 0.22, 12, false));
      var crustMat = matte(0xff8c2e, 0.78);
      var crust = new THREE.Mesh(crustGeo, crustMat); body.add(crust);
      var capL = new THREE.Mesh(new THREE.SphereGeometry(0.22, 14, 10), crustMat), capR = capL.clone();
      capL.position.copy(crustPts[0]); capR.position.copy(crustPts[12]); body.add(capL, capR);
      // toppings follow the cheese surface
      var topMat = gloss(0xff3d8b, { roughness: 0.5, clearcoat: 0.4 });
      var anchors = [[0.25, -0.42, 0.21], [0.27, 0.42, 0.21], [0.55, 0.0, 0.19], [0.78, 0.05, 0.12]];
      var tops = anchors.map(function (an) { var m = new THREE.Mesh(new THREE.SphereGeometry(an[2], 18, 10), topMat); m.scale.z = 0.38; body.add(m); return m; });
      var P0 = new THREE.Vector3(), Pu = new THREE.Vector3(), Pv2 = new THREE.Vector3(), Nn = new THREE.Vector3(), Z = new THREE.Vector3(0, 0, 1), Qt = new THREE.Quaternion();
      var pos = geo.attributes.position.array, cbase = crustGeo.userData.base, cpos = crustGeo.attributes.position.array;
      return {
        root: root, bottom: 1.15, heroScale: 1.05, k: 55, c: 4.4, lag: [32, 2.4],
        update: function (s, aux) {
          state.s = s; state.bend = aux;
          for (var i = 0; i < uv.length / 3; i++) {
            basePt(uv[i * 3], uv[i * 3 + 1], uv[i * 3 + 2], tv); deform(uv[i * 3], tv);
            pos[i * 3] = tv.x; pos[i * 3 + 1] = tv.y; pos[i * 3 + 2] = tv.z;
          }
          geo.attributes.position.needsUpdate = true; geo.computeVertexNormals();
          for (var j = 0; j < cbase.length / 3; j++) {           // crust resists: only a small squash
            var x = cbase[j * 3], y = cbase[j * 3 + 1], z = cbase[j * 3 + 2];
            cpos[j * 3] = x * (1 + 0.05 * s); cpos[j * 3 + 1] = y - 0.05 * s * (1 - x * x); cpos[j * 3 + 2] = z * (1 - 0.14 * s);
          }
          crustGeo.attributes.position.needsUpdate = true; crustGeo.computeVertexNormals();
          capL.scale.set(1, 1, 1 - 0.14 * s); capR.scale.copy(capL.scale);
          anchors.forEach(function (an, k) {
            basePt(an[0], an[1], 1, P0); deform(an[0], P0);
            basePt(an[0] + 0.02, an[1], 1, Pu); deform(an[0] + 0.02, Pu);
            basePt(an[0], an[1] + 0.03, 1, Pv2); deform(an[0], Pv2);
            Nn.crossVectors(Pv2.sub(P0), Pu.sub(P0)).normalize();
            if (Nn.z < 0) Nn.negate();
            tops[k].position.copy(P0).addScaledVector(Nn, 0.035);
            tops[k].quaternion.copy(Qt.setFromUnitVectors(Z, Nn));
            tops[k].scale.set(1 + 0.12 * s, 1 + 0.12 * s, 0.38 * (1 - 0.3 * Math.max(s, 0)));
          });
        }
      };
    }

    // ---------- 5. Seasonal minis (simplified models) ----------
    function makeTree() {
      var root = new THREE.Group(), body = new THREE.Group(); body.position.y = -0.9; root.add(body);
      var tex = speckle('#1fae4f', ['#ffffff', '#fff3a0', '#b6f23a'], 260);
      var mat = new THREE.MeshPhysicalMaterial({ color: 0x9cffb5, map: tex, transparent: true, opacity: 0.9, roughness: 0.25, clearcoat: 1 });
      var tiers = [[0.95, 0.85, 0.42], [0.74, 0.75, 0.95], [0.52, 0.62, 1.42]].map(function (d) {
        var m = new THREE.Mesh(new THREE.ConeGeometry(d[0], d[1], 28), mat); m.position.y = d[2]; body.add(m); return { m: m, y: d[2], h: d[1] };
      });
      var starGeo = new THREE.ExtrudeGeometry(starShape(0.3, 0.13), { depth: 0.1, bevelEnabled: true, bevelThickness: 0.04, bevelSize: 0.03, bevelSegments: 2 }); starGeo.center();
      var star = new THREE.Mesh(starGeo, gloss(0xe8eef7, { metalness: 0.6, roughness: 0.2 })); star.position.y = 1.95; body.add(star);
      return {
        root: root, bottom: 0.9, heroScale: 1.15, k: 110, c: 9, lag: [40, 2.6],
        update: function (s, aux) {
          tiers.forEach(function (tr, i) {   // accordion: tiers gather toward the base
            tr.m.scale.set(1 + 0.22 * s, 1 - 0.4 * s, 1 + 0.22 * s);
            tr.m.position.y = tr.y * (1 - 0.42 * s);
          });
          star.position.y = 1.95 * (1 - 0.42 * s);
          star.rotation.z = (aux - s) * 1.2; star.rotation.y = (aux - s) * 0.8;
        }
      };
    }
    function makeCandy() {
      var root = new THREE.Group();
      var tex = speckle('#ff4fa8', ['#ffffff', '#ffd1ec', '#c79bff'], 300);
      var mat = new THREE.MeshPhysicalMaterial({ color: 0xffb0dc, map: tex, transparent: true, opacity: 0.88, roughness: 0.22, clearcoat: 1 });
      var ball = new THREE.Mesh(new THREE.SphereGeometry(0.66, 32, 24), mat); root.add(ball);
      var wrapProf = [[0.12, 0], [0.16, 0.12], [0.3, 0.32], [0.48, 0.5], [0.52, 0.56]].map(function (p) { return new THREE.Vector2(p[0], p[1]); });
      var endGeo = new THREE.LatheGeometry(wrapProf, 20);
      var endMat = new THREE.MeshPhysicalMaterial({ color: 0xffc2e4, transparent: true, opacity: 0.7, roughness: 0.15, clearcoat: 1, side: THREE.DoubleSide });
      var ends = [-1, 1].map(function (side) {
        var g = new THREE.Group(); var m = new THREE.Mesh(endGeo, endMat); m.rotation.z = side * -PI / 2; g.add(m); g.position.x = side * 0.62; root.add(g); return { g: g, side: side };
      });
      return {
        root: root, bottom: 0.7, heroScale: 1.15, k: 130, c: 8, lag: [45, 3],
        update: function (s, aux) {
          ball.scale.set(1 + 0.2 * s, 1 - 0.38 * s, 1 + 0.15 * s);
          ends.forEach(function (e) {
            e.g.position.x = e.side * 0.62 * (1 + 0.2 * s);
            e.g.rotation.x = e.side * aux * 2.2;                // wrapper twists
            var flap = 1 + 0.3 * (aux - s); e.g.scale.set(1, flap, flap);
          });
        }
      };
    }
    function makeGlobe() {
      var root = new THREE.Group();
      var gGeo = storeBase(new THREE.SphereGeometry(0.85, 36, 26));
      var glass = new THREE.Mesh(gGeo, new THREE.MeshPhysicalMaterial({ color: 0xffffff, transparent: true, opacity: 0.24, roughness: 0.05, clearcoat: 1, depthWrite: false }));
      glass.position.y = 0.2; glass.renderOrder = 2; root.add(glass);
      var NP = mobile ? 90 : 150, pp = [], pc = [], cl = [new THREE.Color(0xffffff), new THREE.Color(0x9ff3ee), new THREE.Color(0xff9ad5), new THREE.Color(0xd9ccff)];
      for (var i = 0; i < NP; i++) { var v = new THREE.Vector3(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5).normalize().multiplyScalar(Math.cbrt(Math.random()) * 0.68); pp.push(v.x, v.y, v.z); var c = cl[i % 4]; pc.push(c.r, c.g, c.b); }
      var pg = new THREE.BufferGeometry(); pg.setAttribute('position', new THREE.Float32BufferAttribute(pp, 3)); pg.setAttribute('color', new THREE.Float32BufferAttribute(pc, 3));
      var glitter = new THREE.Points(pg, new THREE.PointsMaterial({ size: 0.07, vertexColors: true, transparent: true, depthWrite: false }));
      glitter.position.y = 0.2; root.add(glitter);
      var baseProf = [[0.001, -0.95], [0.66, -0.95], [0.7, -0.9], [0.62, -0.62], [0.5, -0.55], [0.001, -0.55]].map(function (p) { return new THREE.Vector2(p[0], p[1]); });
      var stand = new THREE.Mesh(new THREE.LatheGeometry(baseProf, 32), gloss(0x8b5cf6, { roughness: 0.3 })); root.add(stand);
      var gb = gGeo.userData.base, gp = gGeo.attributes.position.array, prevS = 0, swirl = 0;
      return {
        root: root, bottom: 0.95, heroScale: 1.25, k: 90, c: 9,
        update: function (s, aux, dt) {
          for (var j = 0; j < gb.length / 3; j++) {
            var x = gb[j * 3], y = gb[j * 3 + 1], z = gb[j * 3 + 2], h = (y + 0.85) / 1.7;
            var w = 1 + 0.18 * s * Math.sin(PI * clamp(h, 0, 1));
            gp[j * 3] = x * w; gp[j * 3 + 1] = -0.85 + (y + 0.85) * (1 - 0.3 * s); gp[j * 3 + 2] = z * w;
          }
          gGeo.attributes.position.needsUpdate = true; gGeo.computeVertexNormals();
          swirl += (s - prevS) * 28; prevS = s; swirl *= Math.exp(-dt * 1.4);
          glitter.rotation.y += swirl * dt; glitter.rotation.x += swirl * dt * 0.3;
          glitter.scale.set(1 + 0.15 * s, 1 - 0.28 * s, 1 + 0.15 * s); glitter.position.y = 0.2 - 0.2 * s;
          stand.scale.set(1 + 0.03 * s, 1 - 0.05 * s, 1 + 0.03 * s);
        },
        busy: function () { return Math.abs(swirl) > 0.01; }
      };
    }
    // ---------- 6. Color Change NeeDoh: soft matte ball; the stretched bulges change color ----------
    // Squeezed, it flattens and pushes out between the fingers; the color shift shows up in those
    // stretched areas and fades back as the ball relaxes (blue -> pink, pink -> purple).
    function makeColorChange() {
      var root = new THREE.Group();
      var seg = mobile ? 40 : 56;
      var geo = storeBase(new THREE.SphereGeometry(1, seg, Math.round(seg * 0.75)));
      var base = geo.userData.base, n = base.length / 3, pos = geo.attributes.position.array;
      var col = new Float32Array(n * 3); geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
      // where the squish bulges out between fingers
      var lobes = [[0.95, 0.12, 0.3], [-0.65, 0.3, 0.7], [-0.5, -0.25, -0.85], [0.35, -0.5, -0.8], [0.15, 0.55, 0.85], [0.6, 0.45, -0.65]]
        .map(function (p) { return new THREE.Vector3(p[0], p[1], p[2]).normalize(); });
      var w = new Float32Array(n), v = new THREE.Vector3();
      for (var i = 0; i < n; i++) {
        v.set(base[i * 3], base[i * 3 + 1], base[i * 3 + 2]);
        var m = 0; for (var L = 0; L < lobes.length; L++) m = Math.max(m, smooth(0.6, 0.96, v.dot(lobes[L])));
        w[i] = m;
      }
      var mat = new THREE.MeshPhysicalMaterial({ color: 0xffffff, vertexColors: true, roughness: 0.58, clearcoat: 0.3, clearcoatRoughness: 0.45, bumpMap: bumpTex, bumpScale: 0.01 });
      root.add(new THREE.Mesh(geo, mat));
      var cA = new THREE.Color(), cB = new THREE.Color(), cc = new THREE.Color(), last = [0, 0];
      var colorways = [{ a: 0x29b3f2, b: 0xff4fb0 }, { a: 0xff4fb0, b: 0x9a4dff }];
      var self = {
        root: root, radius: 1, heroScale: 1.15, k: 70, c: 6, lag: [26, 2.6],
        colorways: colorways,
        setColor: function (cw) { setHexLin(cA, cw.a); setHexLin(cB, cw.b); self.update(last[0], last[1]); },
        update: function (s, aux) {
          last[0] = s; last[1] = aux;
          var sp = Math.max(s, -0.2), heat = clamp(Math.max(aux, s), 0, 1.05);
          for (var i2 = 0; i2 < n; i2++) {
            var x = base[i2 * 3], y = base[i2 * 3 + 1], z = base[i2 * 3 + 2];
            var r = 1 + sp * (0.08 + 0.46 * w[i2]), ys = 1 - 0.3 * sp;
            pos[i2 * 3] = x * r; pos[i2 * 3 + 1] = y * r * ys; pos[i2 * 3 + 2] = z * r;
            // the color change lags a little behind the shape, so it fades out as the ball relaxes
            var k = smooth(0.12, 0.8, heat * (0.15 + 1.05 * w[i2]));
            cc.copy(cA).lerp(cB, k);
            col[i2 * 3] = cc.r; col[i2 * 3 + 1] = cc.g; col[i2 * 3 + 2] = cc.b;
          }
          geo.attributes.position.needsUpdate = true; geo.attributes.color.needsUpdate = true; geo.computeVertexNormals();
        }
      };
      setHexLin(cA, colorways[0].a); setHexLin(cB, colorways[0].b);
      return self;
    }

    // ---------- 7. NeeDoh Dohnut: soft dough ring + drippy frosting + sprinkles ----------
    function makeDohnut() {
      var root = new THREE.Group(), body = new THREE.Group(); body.rotation.x = 0.45; root.add(body);
      var RR = 0.62, rt = 0.38, NU = mobile ? 40 : 64, NV = mobile ? 16 : 24;
      function ringPt(u, vv, tube, out) { var rr = RR + tube * Math.cos(vv); return out.set(rr * Math.cos(u), tube * Math.sin(vv), rr * Math.sin(u)); }
      var dGeo = new THREE.TorusGeometry(RR, rt, NV, NU); dGeo.rotateX(PI / 2); storeBase(dGeo);
      var doughMat = gloss(0xff5fb2, { roughness: 0.5, clearcoat: 0.35, clearcoatRoughness: 0.5 });
      body.add(new THREE.Mesh(dGeo, doughMat));
      // frosting: top of the ring with a wavy, drippy edge (outer drips hang lower than inner ones)
      var fp = [], fi = [], tv = new THREE.Vector3(), NV2 = mobile ? 10 : 14, ft = rt * 1.07, edge = [];
      for (var iu = 0; iu <= NU; iu++) {
        var u = (iu / NU) * PI * 2, d = 0.22 + 0.16 * Math.sin(7 * u) + 0.07 * Math.sin(13 * u + 1.3);
        for (var iv = 0; iv <= NV2; iv++) {
          var vv = lerp(-d, PI + d * 0.55, iv / NV2);
          ringPt(u, vv, ft, tv); fp.push(tv.x, tv.y, tv.z);
        }
        edge.push(ringPt(u, -d, ft * 0.985, new THREE.Vector3()));
      }
      for (iu = 0; iu < NU; iu++) for (iv = 0; iv < NV2; iv++) {
        var a = iu * (NV2 + 1) + iv, b = a + NV2 + 1;
        fi.push(a, b, a + 1, a + 1, b, b + 1);
      }
      var fGeo = new THREE.BufferGeometry(); fGeo.setAttribute('position', new THREE.Float32BufferAttribute(fp, 3)); fGeo.setIndex(fi); fGeo.computeVertexNormals(); storeBase(fGeo);
      var frostMat = gloss(0x39a6ff, { roughness: 0.22, clearcoat: 1, clearcoatRoughness: 0.1, side: THREE.DoubleSide });
      body.add(new THREE.Mesh(fGeo, frostMat));
      var lipGeo = storeBase(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(edge, true), NU * 2, 0.045, 8, true));
      body.add(new THREE.Mesh(lipGeo, frostMat));
      // sprinkles stuck to the frosting
      var NS = mobile ? 60 : 110, spr = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.03, 0.03, 0.14, 6), gloss(0xffffff, { roughness: 0.35 }), NS);
      var SC = [0xff2e93, 0xffd23f, 0x2fe0d6, 0xffffff, 0xb6f23a, 0xff8a1f, 0x9a4dff], tmpC = new THREE.Color(), sd = [];
      var UP = new THREE.Vector3(0, 1, 0), M = new THREE.Matrix4(), Q = new THREE.Quaternion(), S1 = new THREE.Vector3(1, 1, 1), P = new THREE.Vector3(), Nn = new THREE.Vector3();
      for (var si = 0; si < NS; si++) {
        var su = Math.random() * PI * 2, sv = 0.35 + Math.random() * (PI - 0.7);
        var p0 = ringPt(su, sv, ft + 0.02, new THREE.Vector3()), c0 = new THREE.Vector3(RR * Math.cos(su), 0, RR * Math.sin(su));
        var nrm = p0.clone().sub(c0).normalize(), tan = new THREE.Vector3().crossVectors(nrm, new THREE.Vector3(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5)).normalize();
        sd.push({ p: p0, q: new THREE.Quaternion().setFromUnitVectors(UP, tan) });
        spr.setColorAt(si, setHexLin(tmpC, SC[si % SC.length]));
      }
      spr.instanceColor.needsUpdate = true; body.add(spr);
      // squeezed from above: flattens, spreads, and the frosting follows the dough a beat later
      function squash(x, y, z, s, wob, out) {
        var ang = Math.atan2(z, x), spread = 1 + s * (0.14 + 0.06 * Math.cos(2 * ang)) + wob * Math.sin(3 * ang);
        return out.set(x * spread, y * (1 - 0.38 * s), z * spread);
      }
      var parts = [dGeo, fGeo, lipGeo];
      var self = {
        root: root, radius: 1, heroScale: 1.15, k: 58, c: 5.2, lag: [24, 2.4],
        colorways: [{ d: 0xff5fb2, f: 0x39a6ff }, { d: 0xffc93a, f: 0xff3d9e }],
        setColor: function (cw) { setHexLin(doughMat.color, cw.d); setHexLin(frostMat.color, cw.f); },
        update: function (s, aux) {
          var sd2 = Math.max(s, -0.2), sf = Math.max(lerp(s, aux, 0.45), -0.2), wob = clamp(aux - s, -0.5, 0.5) * 0.07;
          parts.forEach(function (g, gi) {
            var bs = g.userData.base, ps = g.attributes.position.array, ss = gi === 0 ? sd2 : sf;
            for (var j = 0; j < bs.length; j += 3) { squash(bs[j], bs[j + 1], bs[j + 2], ss, wob, tv); ps[j] = tv.x; ps[j + 1] = tv.y; ps[j + 2] = tv.z; }
            g.attributes.position.needsUpdate = true; g.computeVertexNormals();
          });
          for (var k = 0; k < NS; k++) { squash(sd[k].p.x, sd[k].p.y, sd[k].p.z, sf, wob, P); M.compose(P, sd[k].q, S1); spr.setMatrixAt(k, M); }
          spr.instanceMatrix.needsUpdate = true;
        }
      };
      return self;
    }

    function starShape(r1, r2) {
      var sh = new THREE.Shape();
      for (var i = 0; i < 10; i++) { var r = i % 2 ? r2 : r1, a = (i / 10) * PI * 2 + PI / 2; if (i === 0) sh.moveTo(Math.cos(a) * r, Math.sin(a) * r); else sh.lineTo(Math.cos(a) * r, Math.sin(a) * r); }
      sh.closePath(); return sh;
    }

    // ---------- background floaters (decor). Index 0 (sphere) and 2 (ring) are now real toys ----------
    var DECOR_ALL = [['sphere', -2.9, 2.6, -2.6], ['star', 2.9, 2.7, -2.4], ['ring', -3.1, -0.4, -3.2], ['sphere', 3.1, 0.2, -3.4], ['ring', 2.2, -1.9, -2.8], ['star', -2.4, -1.9, -3.0]];
    function decorHome(i) {
      var d = DECOR_ALL[i];
      return { x: d[1] * (mobile ? 0.7 : 1), y: d[2], z: d[3], ph: Math.random() * 6, sp: 0.5 + Math.random() * 0.5, rs: 0.3 + Math.random() * 0.4 };
    }

    // ---------- toy registry ----------
    var SHOP_HINT = mobile ? 'Tap to squeeze. Drag it into the basket, or tap Add to cart.' : 'Click to squeeze. Drag it into the basket, or use Add to cart.';
    var DEFS = [
      { id: 'atomic', name: 'Atomic NeeDoh', hint: 'Press and hold to push the squish through the cage.', make: makeAtomic },
      { id: 'gumdrop', name: 'NeeDoh Gumdrop', hint: 'Hold to squish it flat, then let it wobble back.', make: makeGumdrop },
      { id: 'fuzz', name: 'NeeDoh Fuzz Ball', hint: 'Squeeze it and watch the fuzzy waves ripple.', make: makeFuzz },
      { id: 'squeeza', name: 'NeeDoh Squeezza', hint: 'Hold to bend the slice, then let it flop back.', make: makeSqueeza },
      // these two stay where the floating sphere and ring were, with the same float; they never go centre stage
      { id: 'cc', name: 'Color Change NeeDoh', hint: SHOP_HINT, make: makeColorChange, home: 0, size: 0.36 },
      { id: 'dohnut', name: 'NeeDoh Dohnut', hint: SHOP_HINT, make: makeDohnut, home: 2, size: 0.56 },
      { id: 'tree', name: 'Holiday Tree mini', hint: 'Squish the tiers together.', make: makeTree, seasonal: true },
      { id: 'globe', name: 'Snow Globe squish', hint: 'Squeeze to swirl the glitter.', make: makeGlobe, seasonal: true, desktopOnly: true },
      { id: 'candy', name: 'Candy Twist mini', hint: 'Squeeze to twist the wrapper.', make: makeCandy, seasonal: true, desktopOnly: true }
    ];
    var items = [{ id: 'bo', name: 'Bo', hint: 'Tap or hold to give Bo a squish.', isBo: true, k: 180, c: 10 }];
    DEFS.forEach(function (d) {
      if (mobile && d.desktopOnly) return;
      var toy = d.make();
      var it = { id: d.id, name: d.name, hint: d.hint, seasonal: d.seasonal, toy: toy, root: toy.root };
      if (d.home != null) {
        it.inPlace = true; it.home = decorHome(d.home); it.size = d.size; it.ft = 0; it.selS = 0;
        it.spinE = new THREE.Euler(Math.random() * 3, Math.random() * 3, 0);
        toy.root.position.set(it.home.x, it.home.y, it.home.z); toy.root.scale.setScalar(d.size);
      }
      it.hit = new THREE.Mesh(new THREE.SphereGeometry(1.25, 8, 6), new THREE.MeshBasicMaterial({ visible: false }));
      toy.root.add(it.hit);
      scene.add(toy.root);
      it.cw = 0;
      if (toy.colorways) {
        toy.setColor(toy.colorways[0]); // already converted to linear by setHexLin
        toy.root.traverse(function (o) { if (o.material) o.material.__lin = true; });
      }
      toy.update(0, 0, 0, 0);
      items.push(it);
    });
    items.forEach(function (it, i) {
      var k = it.isBo ? it.k : it.toy.k, c = it.isBo ? it.c : it.toy.c;
      it.sq = new Spring(k, c);                                // squeeze amount
      if (!it.isBo && it.toy.lag) it.lag = new Spring(it.toy.lag[0], it.toy.lag[1]);
      it.sel = new Spring(95, 11.5, i === 0 ? 1 : 0);           // 0 = orbit, 1 = centre stage
      it.hover = 0; it.dirty = true;
    });
    var toyItems = items.filter(function (x) { return !x.isBo && !x.inPlace; }), NT = toyItems.length;
    var placeItems = items.filter(function (x) { return x.inPlace; });
    toyItems.forEach(function (it, i) {
      var even = i % 2 === 0;
      it.orbit = {
        ang: (i / NT) * PI * 2 + 0.4, speed: 0.13 + 0.045 * (i % 3), b: 1.7 + 0.25 * (i % 2),
        yMid: even ? 0.35 : 0.25, yAmp: even ? -1.95 : 1.85,   // front pass goes below Bo's feet or above his head
        bobF: 0.7 + 0.25 * i, bobP: i * 1.3, spin: new THREE.Vector3(0.25 + 0.1 * (i % 3), 0.35 + 0.08 * i, 0.1)
      };
      it.spinE = new THREE.Euler(Math.random() * 3, Math.random() * 3, 0);
    });

    // ---------- background decor: a few balls, stars and rings ----------
    var decor = [];
    var COLORS = [0xff2e93, 0xb6f23a, 0xb9a4ff, 0x2fe0d6, 0xffd23f];
    var sGeo = new THREE.ExtrudeGeometry(starShape(0.42, 0.19), { depth: 0.16, bevelEnabled: true, bevelThickness: 0.06, bevelSize: 0.05, bevelSegments: 3 }); sGeo.center();
    var decSpecs = DECOR_ALL.map(function (d, i) { return { d: d, i: i }; }).filter(function (o) { return (!mobile || o.i < 3) && o.i !== 0 && o.i !== 2; });
    decSpecs.forEach(function (o) {
      var d = o.d, i = o.i;
      var g = d[0] === 'ring' ? new THREE.TorusGeometry(0.42, 0.13, 16, 40) : d[0] === 'star' ? sGeo : new THREE.SphereGeometry(0.34, 24, 18);
      var m = new THREE.Mesh(g, gloss(COLORS[(i + 1) % 5], { roughness: 0.2 }));
      m.position.set(d[1] * (mobile ? 0.7 : 1), d[2], d[3]);
      m.userData = { base: m.position.clone(), ph: Math.random() * 6, sp: 0.5 + Math.random() * 0.5, rs: 0.3 + Math.random() * 0.4 };
      scene.add(m); decor.push(m);
    });
    var toyShadow = blobShadow();

    // =====================================================================
    //  STATE, INPUT & UI
    // =====================================================================
    var motionOn = !document.documentElement.classList.contains('bb-still');
    var active = 0, hovered = -1, kbFocus = -1;
    var press = { on: false, t0: 0, minUntil: 0, id: null, peak: 0, item: null };
    var drag = { it: null, z: 0 };   // toy currently being carried toward the basket
    var pointer = { x: 0, y: 0 }, smoothP = { x: 0, y: 0 };
    var t = 0, clock = new THREE.Clock(), running = false, raf = 0, visible = true, onceUntil = 0;
    var nextBlink = 2.5, blinkT = -1, react = null, lastReact = '', bubbleTimer = 0, lastLine = 0;
    var CENTER = new THREE.Vector3(0, -0.05, 0.55), BO_SIDE = mobile ? new THREE.Vector3(-1.95, -1.25, 0.6) : new THREE.Vector3(-2.35, -0.95, 0.5);
    var BO_SIDE_SCALE = mobile ? 0.36 : 0.42;
    var ray = new THREE.Raycaster(), ndc = new THREE.Vector2(), v3 = new THREE.Vector3(), q1 = new THREE.Quaternion(), q2 = new THREE.Quaternion(), qPresent = new THREE.Quaternion();

    function current() { return items[active]; }
    function announce(msg) { if (ui.live) { ui.live.textContent = ''; setTimeout(function () { ui.live.textContent = msg; }, 30); } }
    function say(text, ms) {
      if (!ui.bubble) return;
      ui.bubble.textContent = text; ui.bubble.classList.add('is-show');
      clearTimeout(bubbleTimer); bubbleTimer = setTimeout(function () { ui.bubble.classList.remove('is-show'); }, ms || 1500);
    }

    // build the selector
    if (ui.chips) {
      ui.chips.innerHTML = '';
      items.forEach(function (it, i) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'bb-chip' + (it.isBo ? ' bb-chip--bo' : '');
        b.setAttribute('aria-pressed', i === active ? 'true' : 'false');
        b.innerHTML = '<span class="bb-chip__dot" aria-hidden="true"></span><span>' + it.name + (it.seasonal ? ' <em>seasonal</em>' : '') + '</span>';
        b.addEventListener('click', function () { select(i); });
        b.addEventListener('focus', function () { kbFocus = i; kick(); });
        b.addEventListener('blur', function () { if (kbFocus === i) kbFocus = -1; });
        b.addEventListener('mouseenter', function () { kbFocus = i; kick(); });
        b.addEventListener('mouseleave', function () { if (kbFocus === i) kbFocus = -1; });
        it.chip = b; ui.chips.appendChild(b);
      });
    }
    function refreshUI() {
      var it = current();
      items.forEach(function (x, i) { if (x.chip) x.chip.setAttribute('aria-pressed', i === active ? 'true' : 'false'); });
      if (ui.name) ui.name.textContent = it.name;
      if (ui.hint) ui.hint.textContent = it.hint;
      if (ui.back) ui.back.hidden = it.isBo;
      if (ui.color) ui.color.hidden = !(it.toy && it.toy.colorways);
      if (ui.squish) ui.squish.setAttribute('aria-label', 'Squish ' + it.name);
      refreshShop();
      if (it.chip && ui.chips) {
        var cr = it.chip.getBoundingClientRect(), pr = ui.chips.getBoundingClientRect();
        if (cr.left < pr.left || cr.right > pr.right) ui.chips.scrollLeft += cr.left - pr.left - (pr.width - cr.width) / 2;
      }
    }
    function select(i) {
      i = (i + items.length) % items.length;
      if (i === active) return;
      release(true);
      var prev = active; active = i;
      items.forEach(function (it, k) { if (!motionOn) it.sel.snap(k === active ? 1 : 0); });
      refreshUI();
      announce(current().name + ' selected. ' + current().hint);
      if (current().isBo) say('I’m back! Give me a squish!', 1600);
      else { if (ui.bubble) ui.bubble.classList.remove('is-show'); boReact(prev === 0 ? 'nod' : null); }
      kick();
    }
    if (ui.prev) ui.prev.addEventListener('click', function () { select(active - 1); });
    if (ui.next) ui.next.addEventListener('click', function () { select(active + 1); });
    if (ui.back) ui.back.addEventListener('click', function () { select(0); if (items[0].chip) items[0].chip.focus(); });
    if (ui.color) ui.color.addEventListener('click', function () {
      var it = current(); if (!it.toy || !it.toy.colorways) return;
      it.cw = (it.cw + 1) % it.toy.colorways.length; it.toy.setColor(it.toy.colorways[it.cw]);
      announce('New color'); kick(300);
    });

    // ---------- squeeze input (pointer capture, always released) ----------
    function startPress(src) {
      if (press.on) return;
      press.on = true; press.src = src; press.t0 = performance.now(); press.minUntil = press.t0 + 150; press.peak = 0; press.item = current();
      stage.classList.add('is-squeezing'); kick();
    }
    function release(silent) {
      if (!press.on) return;
      press.on = false; press.id = null; stage.classList.remove('is-squeezing');
      var it = press.item, peak = press.peak;
      press.endAt = Math.max(performance.now(), press.minUntil);
      if (!silent && peak > 0.22) celebrate(it, peak);
      kick();
    }
    if (ui.pad) {
      ui.pad.addEventListener('pointerdown', function (e) {
        if (e.button > 0) return;
        e.preventDefault();
        try { ui.pad.setPointerCapture(e.pointerId); } catch (err) {}
        press.id = e.pointerId; startPress('pointer');
      });
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(function (ev) {
        ui.pad.addEventListener(ev, function (e) { if (press.src === 'pointer' && (press.id === null || e.pointerId === press.id)) release(ev !== 'pointerup'); });
      });
      ui.pad.addEventListener('contextmenu', function (e) { e.preventDefault(); });
      ui.pad.addEventListener('pointermove', function (e) { if (e.pointerType === 'mouse') setPointer(e); });
    }
    if (ui.squish) {
      var keyDown = false, viaKeyOrPointer = false;
      ui.squish.addEventListener('keydown', function (e) {
        if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) { e.preventDefault(); keyDown = true; viaKeyOrPointer = true; startPress('key'); }
      });
      ui.squish.addEventListener('keyup', function (e) {
        if ((e.key === ' ' || e.key === 'Enter') && keyDown) { e.preventDefault(); keyDown = false; viaKeyOrPointer = false; release(false); }
      });
      ui.squish.addEventListener('pointerdown', function (e) {
        try { ui.squish.setPointerCapture(e.pointerId); } catch (err) {}
        viaKeyOrPointer = true; press.id = e.pointerId; startPress('button');
      });
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(function (ev) {
        ui.squish.addEventListener(ev, function () { if (press.src === 'button') release(ev !== 'pointerup'); });
      });
      ui.squish.addEventListener('blur', function () { if (press.src === 'key') { keyDown = false; release(true); } });
      ui.squish.addEventListener('click', function () {
        // assistive tech may send a click with no key/pointer events: do one quick squish
        if (viaKeyOrPointer) { viaKeyOrPointer = false; return; }
        startPress('click'); setTimeout(function () { release(false); }, 160);
      });
    }
    window.addEventListener('blur', function () { release(true); });
    document.addEventListener('visibilitychange', function () { if (document.hidden) release(true); });

    // ---------- pointing: hover pauses an orbiting toy, tap selects it ----------
    function setPointer(e) {
      var r = stage.getBoundingClientRect();
      pointer.x = ((e.clientX - r.left) / r.width) * 2 - 1;
      pointer.y = -(((e.clientY - r.top) / r.height) * 2 - 1);
      return r;
    }
    function pick(e) {
      var r = stage.getBoundingClientRect();
      ndc.set(((e.clientX - r.left) / r.width) * 2 - 1, -(((e.clientY - r.top) / r.height) * 2 - 1));
      ray.setFromCamera(ndc, camera);
      // among the toys under the pointer, take the one whose centre is closest on screen (relative to its
      // size), so a small toy floating at the back can still be picked when an orbiting toy passes nearby
      var best = -1, bd = 1e9, c = new THREE.Vector3(), edge = new THREE.Vector3();
      items.forEach(function (it, i) {
        if (i === active) return;
        var hit = it.isBo ? boHit : it.hit, h = ray.intersectObject(hit, false);
        if (!h.length) return;
        hit.getWorldPosition(c); var rad = hit.geometry.parameters.radius * hit.getWorldScale(edge).x;
        edge.copy(c).add(new THREE.Vector3(rad, 0, 0)).project(camera); c.project(camera);
        var pr = Math.max(1e-4, Math.abs(edge.x - c.x)), d = Math.hypot(ndc.x - c.x, (ndc.y - c.y) * H / W) / pr;
        if (d < bd) { bd = d; best = i; }
      });
      return best;
    }
    var down = null;
    stage.addEventListener('pointermove', function (e) {
      if (e.target === ui.pad) return;
      setPointer(e);
      if (e.pointerType === 'mouse') { var h = pick(e); if (h !== hovered) { hovered = h; stage.style.cursor = h >= 0 ? 'pointer' : ''; kick(); } }
    });
    stage.addEventListener('pointerleave', function () { hovered = -1; stage.style.cursor = ''; if (ui.tip) ui.tip.hidden = true; });
    function onBasket(e) { return e.target && e.target.closest && e.target.closest('[data-bb-basket]'); }
    stage.addEventListener('pointerdown', function (e) {
      if (e.target === ui.pad || onBasket(e)) { down = null; return; }
      // mouse/pen: press a shop toy directly to squeeze it, or drag it straight to the basket
      if (e.pointerType !== 'touch' && e.button === 0 && !press.on) {
        var h0 = pick(e);
        if (h0 >= 0 && items[h0].inPlace) {
          e.preventDefault(); down = null; select(h0);
          try { stage.setPointerCapture(e.pointerId); } catch (err) {}
          press.id = e.pointerId; startPress('stage'); grabStart(e, items[h0]);
          return;
        }
      }
      down = { x: e.clientX, y: e.clientY, t: performance.now() };
    });
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(function (ev) {
      stage.addEventListener(ev, function (e) { if (press.src === 'stage' && (press.id === null || e.pointerId === press.id)) release(ev !== 'pointerup'); });
    });
    stage.addEventListener('pointerup', function (e) {
      if (!down || e.target === ui.pad) return;
      var moved = Math.hypot(e.clientX - down.x, e.clientY - down.y), dt = performance.now() - down.t; down = null;
      if (moved < 10 && dt < 500) {
        var h = pick(e);
        if (h >= 0) {
          select(h);
          // tapping a shop toy squeezes it in place (it never adds anything)
          if (items[h].inPlace) { startPress('click'); setTimeout(function () { release(false); }, 160); }
        }
      }
    });
    window.addEventListener('pointermove', function (e) {
      if (stage.contains(e.target)) return;
      pointer.x = (e.clientX / window.innerWidth) * 2 - 1; pointer.y = -((e.clientY / window.innerHeight) * 2 - 1);
      if (!motionOn) return; kick(150);
    }, { passive: true });

    // =====================================================================
    //  SHOP: real products, drag to the basket, or Add to cart
    //  A tap/click only squeezes. Only an intentional drop on the basket (or the
    //  Add to cart button) adds one unit, and only after Shopify confirms it.
    // =====================================================================
    var shop = {
      el: stage.querySelector('[data-bb-basket]'), count: stage.querySelector('[data-bb-basket-count]'), toast: stage.querySelector('[data-bb-basket-toast]'),
      row: hero.querySelector('[data-bb-shop-row]'), add: hero.querySelector('[data-bb-cart-add]'), variant: hero.querySelector('[data-bb-variant]'),
      busy: false, toastT: 0, n: 0
    };
    var shopData = {};
    try { var sdEl = (hero.parentElement || document).querySelector('[data-bb-shop-data]'); if (sdEl) shopData = JSON.parse(sdEl.textContent) || {}; } catch (err) { shopData = {}; }
    items.forEach(function (it) { var p = shopData[it.id]; if (p && p.variants && p.variants.length) it.product = p; });
    var shopOn = !!shop.el, DRAG_PX = 10, grab = null, offs = [];
    if (shop.count) shop.n = parseInt(shop.count.textContent, 10) || 0;
    function on(target, type, fn, opt) { target.addEventListener(type, fn, opt); offs.push(function () { target.removeEventListener(type, fn, opt); }); }
    function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

    function availVariants(p) { return p.variants.filter(function (v) { return v.available; }); }
    function chosenVariant(it) {
      var p = it.product; if (!p) return null;
      var av = availVariants(p); if (!av.length) return null;
      if (p.variants.length === 1) return av[0];
      if (shop.variant && shop.variant.dataset.for === it.id && shop.variant.value) {
        var v = p.variants.filter(function (x) { return String(x.id) === shop.variant.value; })[0];
        return v && v.available ? v : null;
      }
      return null;                              // more than one option: the shopper has to pick first
    }
    function setCount(n) {
      if (!shop.el || typeof n !== 'number') return;
      shop.n = n; shop.count.textContent = n; shop.el.classList.toggle('has-items', n > 0);
      shop.el.setAttribute('aria-label', 'Drop to cart. ' + n + (n === 1 ? ' item' : ' items') + ' in your cart. Open cart');
    }
    function toast(msg, isErr) {
      if (!shop.toast) return;
      clearTimeout(shop.toastT);
      shop.toast.textContent = msg; shop.toast.classList.toggle('is-error', !!isErr); shop.toast.classList.add('is-show');
      shop.toastT = setTimeout(function () { shop.toast.classList.remove('is-show'); }, isErr ? 4200 : 2200);
    }
    function errText(err, it) {
      if (err && err.kind === 'network') return 'Couldn’t reach the store. Check your connection and try again.';
      return err && err.message && err.message !== 'add failed' ? err.message : 'Couldn’t add ' + it.name + '. Please try again.';
    }
    function shopAdd(variant) {
      var qty = Math.max(1, variant.min || 1);
      if (window.BBShop && window.BBShop.add) return window.BBShop.add(variant.id, qty, stage);
      // fallback if the theme's event module didn't load: plain Ajax Cart API
      var base = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      return fetch(base + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: variant.id, quantity: qty }] }) })
        .then(function (r) {
          return r.json().then(function (j) { if (!r.ok || j.status) { var e = new Error(j.description || j.message || 'add failed'); e.kind = 'shopify'; throw e; } });
        }, function () { var e = new Error('network'); e.kind = 'network'; throw e; })
        .then(function () { return fetch(base + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }).catch(function () { return null; }); })
        .then(function (cart) { if (cart) document.querySelectorAll('.cart-bubble__text-count').forEach(function (el) { el.textContent = cart.item_count; }); return cart; });
    }
    function addToCart(it, fromDrop) {
      if (shop.busy || !it || !it.product) return;   // one add at a time: repeated events can't double-add
      var v = chosenVariant(it);
      if (!v) {
        if (!availVariants(it.product).length) { toast(it.name + ' is sold out', true); announce(it.name + ' is sold out.'); }
        else {
          if (items.indexOf(it) !== active) select(items.indexOf(it));
          toast('Pick an option first', true); announce('Choose an option for ' + it.name + ', then use Add to cart.');
          if (shop.variant && !shop.variant.hidden) shop.variant.focus();
        }
        return;
      }
      shop.busy = true; it.adding = !!fromDrop; kick();
      if (shop.el) shop.el.classList.add('is-busy');
      if (shop.add) { shop.add.disabled = true; shop.add.setAttribute('aria-busy', 'true'); }
      shopAdd(v).then(function (cart) {
        it.adding = false;
        if (fromDrop && motionOn) it.dropT = 0; else if (it.dragB) it.dragB.snap(0);
        if (cart) setCount(cart.item_count);
        toast('Added to cart');
        announce(it.name + ' added to cart.' + (cart ? ' ' + cart.item_count + (cart.item_count === 1 ? ' item' : ' items') + ' in your cart.' : ''));
        if (shop.el) { shop.el.classList.remove('is-bump'); void shop.el.offsetWidth; shop.el.classList.add('is-bump'); }
        boReact('happy');
      }, function (err) {
        it.adding = false;                           // glides back to where it lives; nothing was added
        var m = errText(err, it); toast(m, true); announce(m);
      }).then(function () {
        shop.busy = false; kick(700);
        if (shop.el) shop.el.classList.remove('is-busy');
        if (shop.add) shop.add.removeAttribute('aria-busy');
        refreshShop();
      });
    }
    function refreshShop() {
      if (!shop.row) return;
      var it = current(), p = it.product;
      shop.row.hidden = !p;
      if (!p) return;
      if (shop.variant) {
        var multi = p.variants.length > 1;
        shop.variant.hidden = !multi;
        if (multi && shop.variant.dataset.for !== it.id) {
          shop.variant.dataset.for = it.id;
          shop.variant.innerHTML = '<option value="">Choose an option</option>' + p.variants.map(function (v) {
            return '<option value="' + esc(v.id) + '"' + (v.available ? '' : ' disabled') + '>' + esc(v.title) + (v.available ? '' : ' (sold out)') + '</option>';
          }).join('');
        }
      }
      if (shop.add) {
        var soldOut = !availVariants(p).length;
        shop.add.disabled = soldOut || shop.busy;
        shop.add.textContent = soldOut ? 'Sold out' : 'Add to cart';
        shop.add.setAttribute('aria-label', soldOut ? it.name + ' is sold out' : 'Add ' + it.name + ' to cart');
      }
    }

    // ---- drag: press a purchasable toy and move past the threshold to carry it ----
    function canDrag(it) { return shopOn && !!(it && it.product); }
    function pointerToWorld(e, z, out) {
      var r = stage.getBoundingClientRect();
      ndc.set(((e.clientX - r.left) / r.width) * 2 - 1, -(((e.clientY - r.top) / r.height) * 2 - 1));
      ray.setFromCamera(ndc, camera);
      var o = ray.ray.origin, d = ray.ray.direction;
      return out.copy(o).addScaledVector(d, (z - o.z) / d.z);
    }
    function overBasket(e) {
      if (!shop.el || !e) return false;
      var r = shop.el.getBoundingClientRect(), m = 18;
      return e.clientX >= r.left - m && e.clientX <= r.right + m && e.clientY >= r.top - m && e.clientY <= r.bottom + m;
    }
    function grabStart(e, it) { if (canDrag(it) && !shop.busy) grab = { id: e.pointerId, x: e.clientX, y: e.clientY, it: it }; }
    function grabMove(e) {
      if (!grab || e.pointerId !== grab.id) return;
      if (!drag.it) {
        if (Math.hypot(e.clientX - grab.x, e.clientY - grab.y) < DRAG_PX) return;
        release(true);                               // it's a drag, not a squeeze
        var it = grab.it;
        drag.it = it; drag.z = it.inPlace ? 1.2 : CENTER.z;
        if (!it.dragB) { it.dragB = new Spring(150, 21); it.dragPos = new THREE.Vector3(); }
        it.dropT = -1; it.popT = -1;
        stage.classList.add('is-dragging'); if (shop.el) shop.el.classList.add('is-ready');
        hovered = -1; if (ui.tip) ui.tip.hidden = true;
      }
      pointerToWorld(e, drag.z, drag.it.dragPos);
      var ov = overBasket(e), ok = ov && !!chosenVariant(drag.it);
      if (shop.el) { shop.el.classList.toggle('is-over', ok); shop.el.classList.toggle('is-blocked', ov && !ok); }
      kick();
    }
    function grabEnd(e, cancelled) {
      if (!grab || (e && e.pointerId !== grab.id)) return;
      var it = drag.it, dropped = !!it && !cancelled && overBasket(e);
      grab = null; drag.it = null;
      stage.classList.remove('is-dragging');
      if (shop.el) shop.el.classList.remove('is-ready', 'is-over', 'is-blocked');
      if (it && dropped) addToCart(it, true);        // released outside the basket: it glides home, nothing is added
      kick(900);
    }
    if (ui.pad) {
      on(ui.pad, 'pointerdown', function (e) { if (e.button <= 0) grabStart(e, current()); });
      on(ui.pad, 'pointermove', grabMove);
      on(ui.pad, 'pointerup', function (e) { grabEnd(e, false); });
      on(ui.pad, 'pointercancel', function (e) { grabEnd(e, true); });
      on(ui.pad, 'lostpointercapture', function (e) { grabEnd(e, true); });
    }
    on(stage, 'pointermove', grabMove);
    on(stage, 'pointerup', function (e) { grabEnd(e, false); });
    on(stage, 'pointercancel', function (e) { grabEnd(e, true); });
    on(window, 'blur', function () { grabEnd(null, true); });

    if (shop.add) on(shop.add, 'click', function () { addToCart(current(), false); });
    if (shop.el) on(shop.el, 'click', function () {
      // the store's own cart drawer when it has one, otherwise the cart page
      var d = document.getElementById('cart-drawer');
      if (d && typeof d.open === 'function') d.open(); else location.href = shop.el.getAttribute('data-cart-url') || '/cart';
    });
    on(document, 'bb:cart-count', function (e) { setCount(e.detail && e.detail.count); });
    on(window, 'pageshow', function (e) {
      if (!e.persisted || !shop.el) return;
      fetch(((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); }).then(function (c) { setCount(c.item_count); }).catch(function () {});
    });
    // theme editor: drop this instance's listeners when the section is removed or reloaded
    on(document, 'shopify:section:unload', function (e) {
      if (e.target && e.target.contains && e.target.contains(stage)) { grabEnd(null, true); stop(); offs.splice(0).forEach(function (f) { f(); }); }
    });

    // ---------- delight ----------
    var LINES = ['Nice squish!', 'So squishy!', 'Again?', 'Ooh, squishy!', 'Wobble wobble!'];
    function celebrate(it, peak) {
      if (motionOn) burst(peak);
      var now = performance.now();
      if (now - lastLine > 2200 && (peak > 0.85 || Math.random() < 0.6)) {
        lastLine = now;
        var line = peak > 0.85 ? 'Big stretch!' : LINES[Math.floor(Math.random() * LINES.length)];
        say(line, 1300);
      }
      if (!it.isBo) boReact(null);
      else boReact('happy');
    }
    function burst(peak) {
      if (!ui.stars || !ui.pad) return;
      var pr = ui.pad.getBoundingClientRect(), sr = stage.getBoundingClientRect();
      var cx = pr.left - sr.left + pr.width / 2, cy = pr.top - sr.top + pr.height / 2;
      var n = 6 + Math.round(peak * 4);
      for (var i = 0; i < n; i++) {
        var s = document.createElement('span'), a = (i / n) * PI * 2 + Math.random() * 0.4, d = pr.width * (0.38 + Math.random() * 0.22);
        s.className = 'bb-star bb-star--' + (i % 4);
        s.style.left = cx + 'px'; s.style.top = cy + 'px';
        s.style.setProperty('--x', Math.cos(a) * d + 'px'); s.style.setProperty('--y', Math.sin(a) * d + 'px');
        ui.stars.appendChild(s);
        setTimeout(function (n2) { return function () { n2.remove(); }; }(s), 800);
      }
    }
    function boReact(type) {
      if (!motionOn) return;
      var opts = ['nod', 'hop', 'wave', 'happy'].filter(function (o) { return o !== lastReact; });
      type = type || opts[Math.floor(Math.random() * opts.length)];
      lastReact = type; react = { type: type, t0: t }; kick();
    }

    // ---------- squeeze pad follows the centre item ----------
    function placePad() {
      if (!ui.pad) return;
      var it = current();
      if (it.inPlace) {                 // pad sits over the shop toy where it floats (min. 48px touch target)
        if (drag.it === it) return;
        v3.copy(it.root.position).project(camera);
        var px = (v3.x * 0.5 + 0.5) * W, py = (-v3.y * 0.5 + 0.5) * H;
        var ex = it.root.position.clone().add(new THREE.Vector3(it.toy.radius * it.root.scale.x * 1.35, 0, 0)).project(camera);
        var pr = Math.max(24, Math.abs((ex.x * 0.5 + 0.5) * W - px));
        ui.pad.style.left = (px - pr) + 'px'; ui.pad.style.top = (py - pr) + 'px'; ui.pad.style.width = ui.pad.style.height = (2 * pr) + 'px';
        return;
      }
      if (drag.it === it) return;
      var sc = it.isBo ? 1 : it.toy.heroScale, rad = it.isBo ? 1.75 : 1.25 * sc;
      v3.copy(it.isBo ? new THREE.Vector3(0, 0, 0) : CENTER).project(camera);
      var x = (v3.x * 0.5 + 0.5) * W, y = (-v3.y * 0.5 + 0.5) * H;
      var e = (it.isBo ? new THREE.Vector3(rad, 0, 0) : CENTER.clone().add(new THREE.Vector3(rad, 0, 0))).project(camera);
      var r = Math.abs((e.x * 0.5 + 0.5) * W - x);
      ui.pad.style.left = (x - r) + 'px'; ui.pad.style.top = (y - r) + 'px'; ui.pad.style.width = ui.pad.style.height = (2 * r) + 'px';
    }

    // =====================================================================
    //  FRAME
    // =====================================================================
    var halfW = 3;
    function frame() {
      var dt = Math.min(clock.getDelta(), 0.1), now = performance.now();
      if (motionOn) t += dt;
      smoothP.x += (pointer.x - smoothP.x) * 0.06; smoothP.y += (pointer.y - smoothP.y) * 0.06;
      camera.position.set(camBase.x + smoothP.x * 0.55, camBase.y + smoothP.y * 0.3, camBase.z);
      camera.lookAt(lookAt);
      halfW = Math.tan((camera.fov * PI / 180) / 2) * camBase.z * camera.aspect;
      var orbitA = Math.min(2.6, halfW - 0.75);
      var busy = false;
      var toyActive = !current().isBo && !current().inPlace;   // shop toys are picked in place: Bo keeps centre stage

      // ---- squeeze springs ----
      items.forEach(function (it) {
        var target = 0;
        if (it === press.item && (press.on || now < (press.endAt || 0))) {
          var hold = (now - press.t0) / 1000;
          target = 0.42 + 0.58 * smooth(0.12, 1.4, hold);           // longer hold = more squish, capped
          if (!motionOn) target = Math.min(target, 0.5);
          press.peak = Math.max(press.peak, it.sq.x);
        }
        if (!motionOn) it.sq.c = 2 * Math.sqrt(it.sq.k); else it.sq.c = it.isBo ? 10 : it.toy.c;
        var before = it.sq.x;
        it.sq.step(target, dt);
        if (it.lag) it.lag.step(it.sq.x, dt);
        it.sq.x = clamp(it.sq.x, -0.3, 1.05);
        if (!it.sq.restAt(target) || (it.lag && !it.lag.restAt(target)) || (it.toy && it.toy.busy && it.toy.busy())) { busy = true; it.dirty = true; }
        if (it.dirty && !it.isBo) {
          it.toy.update(it.sq.x, it.lag ? it.lag.x : it.sq.x, dt, t);
          if (it.sq.restAt(0) && (!it.lag || it.lag.restAt(0)) && !(it.toy.busy && it.toy.busy())) it.dirty = false;
        }
        if (before !== it.sq.x) busy = true;
      });

      // ---- selection springs ----
      items.forEach(function (it, i) {
        var target = (i === active && !it.inPlace) || (it.isBo && current().inPlace) ? 1 : 0;
        if (motionOn) it.sel.step(target, dt); else it.sel.snap(target);
        if (!it.sel.restAt(target)) busy = true;
        var hov = (i === hovered || i === kbFocus) && i !== active ? 1 : 0;
        it.hover += (hov - it.hover) * Math.min(1, dt * 10);
        if (Math.abs(it.hover - hov) > 0.01) busy = true;
      });

      // ---- toys: orbit <-> centre ----
      toyItems.forEach(function (it, i) {
        var o = it.orbit, k = it.sel.x;
        var slow = (toyActive ? 0.3 : 1) * (1 - it.hover);
        if (motionOn) o.ang += dt * o.speed * slow;
        var a = orbitA * (0.94 + 0.06 * (i % 2)) * (toyActive ? 1.08 : 1);
        var bob = motionOn ? Math.sin(t * o.bobF + o.bobP) * 0.12 : 0;
        var ox = a * Math.cos(o.ang), oz = -0.3 + o.b * Math.sin(o.ang), oy = o.yMid + o.yAmp * Math.sin(o.ang) + bob;
        it.root.position.set(lerp(ox, CENTER.x, k), lerp(oy, CENTER.y, k), lerp(oz, CENTER.z, k));
        var orbitScale = (mobile ? 0.44 : 0.5) * (1 + 0.18 * it.hover);
        it.root.scale.setScalar(Math.max(0.01, lerp(orbitScale, it.toy.heroScale, k)));
        if (motionOn) { it.spinE.x += dt * o.spin.x * slow; it.spinE.y += dt * o.spin.y * slow; }
        q1.setFromEuler(it.spinE);
        qPresent.setFromEuler(new THREE.Euler(0.12 + (motionOn ? Math.sin(t * 0.8) * 0.06 : 0), (motionOn ? Math.sin(t * 0.5) * 0.25 : 0) + smoothP.x * 0.3, 0));
        q2.copy(q1).slerp(qPresent, clamp(k, 0, 1));
        it.root.quaternion.copy(q2);
      });
      // ---- shop toys: float where the decor sphere/ring floated; paused while squeezed or dragged ----
      placeItems.forEach(function (it, i) {
        var h = it.home, manip = (press.item === it && (press.on || now < (press.endAt || 0))) || drag.it === it || it.adding;
        if (motionOn && !manip) { it.ft += dt; it.spinE.x += dt * h.rs; it.spinE.y += dt * (h.rs + 0.2); }
        it.root.position.set(h.x + smoothP.x * 0.25, h.y + (motionOn ? Math.sin(it.ft * h.sp + h.ph) * 0.18 : 0), h.z);
        it.root.rotation.copy(it.spinE);
        var selT = items.indexOf(it) === active ? 1 : 0;
        it.selS += (selT - it.selS) * Math.min(1, dt * 8);
        if (Math.abs(it.selS - selT) > 0.01) busy = true;
        it.root.scale.setScalar(it.size * (1 + 0.18 * it.hover) * (1 + 0.12 * it.selS));
      });
      // ---- dragging toward the basket, and the drop-in / pop-back animation ----
      items.forEach(function (it) {
        if (!it.dragB) return;
        var holdAtBasket = drag.it === it || it.adding;
        it.dragB.step(holdAtBasket ? 1 : 0, dt);
        if (!it.dragB.restAt(holdAtBasket ? 1 : 0)) busy = true;
        var b = clamp(it.dragB.x, 0, 1.1);
        if (b > 0.0005) it.root.position.lerp(it.dragPos, b);
        if (it.dropT >= 0) {            // shrink into the basket ...
          it.dropT += dt; busy = true;
          var p = clamp(it.dropT / 0.38, 0, 1);
          it.root.scale.multiplyScalar(Math.max(0.02, 1 - 0.95 * p * p)); it.root.position.y -= 0.35 * p;
          if (p >= 1) { it.dropT = -1; it.dragB.snap(0); it.popT = 0; }
        } else if (it.popT >= 0) {      // ... then pop back where it lives, so the scene stays complete
          it.popT += dt; busy = true;
          var q = clamp(it.popT / 0.45, 0, 1), back = 1 + 2.2 * Math.pow(q - 1, 3) + 1.2 * Math.pow(q - 1, 2);
          it.root.scale.multiplyScalar(Math.max(0.02, back));
          if (q >= 1) it.popT = -1;
        }
      });
      if (drag.it) busy = true;

      var actIt = current();
      if (!actIt.isBo && !actIt.inPlace) {
        toyShadow.visible = true;
        var ts = actIt.toy.heroScale * actIt.sel.x;
        toyShadow.position.set(CENTER.x, GROUND + 0.01, CENTER.z);
        toyShadow.scale.set(2.6 * ts * (1 + 0.2 * actIt.sq.x), 1.6 * ts, 1);
        toyShadow.material.opacity = clamp(actIt.sel.x, 0, 1);
      } else toyShadow.visible = false;

      // ---- Bo ----
      var kb = items[0].sel.x, boScale = lerp(BO_SIDE_SCALE, 1, kb);
      var period = 1.5, ph = (t % period) / period, hop = Math.sin(ph * PI);
      var amp = motionOn ? lerp(0.25, 0.55, kb) : 0;
      var baseY = lerp(BO_SIDE.y, 0, kb);
      var extraY = 0, nod = 0, wave = 1, happy = 0;
      if (react) {
        var rp = (t - react.t0) / 0.9;
        if (rp >= 1) react = null; else {
          var e = Math.sin(rp * PI); busy = true;
          if (react.type === 'hop') extraY = 0.7 * e;
          if (react.type === 'nod') nod = 0.35 * Math.sin(rp * PI * 3) * (1 - rp);
          if (react.type === 'wave') wave = 2.2;
          happy = e;
        }
      }
      bo.position.set(lerp(BO_SIDE.x, 0, kb), baseY + (Math.pow(hop, 0.8) * amp + extraY) * boScale, lerp(BO_SIDE.z, 0, kb));
      bo.scale.setScalar(boScale);
      var bs = items[0].sq.x, land = motionOn ? Math.max(0, 1 - hop * 6) : 0, stretch = motionOn ? hop * 0.05 : 0;
      var sy = 1 + stretch - land * 0.1 - 0.34 * bs, sxz = 1 / Math.sqrt(Math.max(0.5, sy));
      boSquish.scale.set(sxz, sy, sxz);
      var faceY = toyActive ? 0.55 : smoothP.x * 0.45;
      bo.rotation.y += (faceY - bo.rotation.y) * 0.08;
      boBody.rotation.x += ((-smoothP.y * 0.18 + nod) - boBody.rotation.x) * 0.15;
      boPupils.forEach(function (p) { p.position.x = toyActive ? 0.06 : smoothP.x * 0.1; p.position.y = toyActive ? -0.02 : smoothP.y * 0.08; });
      var squint = Math.max(happy, bs * 0.9);
      if (motionOn) {
        if (blinkT < 0 && t > nextBlink) blinkT = 0;
        if (blinkT >= 0) { blinkT += dt; var bl = blinkT < 0.07 ? 1 - blinkT / 0.07 : Math.min(1, (blinkT - 0.07) / 0.08); boEyes.scale.y = Math.max(0.08, bl); if (blinkT > 0.16) { blinkT = -1; nextBlink = t + 2.2 + Math.random() * 3; } }
        else boEyes.scale.y = 1 - 0.45 * squint;
      } else boEyes.scale.y = 1 - 0.45 * squint;
      boSmile.scale.set(1 + 0.25 * squint, 1 + 0.25 * squint, 1);
      boTongue.scale.set(1.2, 0.6 + 0.5 * bs, 0.5);
      boArmL.rotation.z = motionOn ? -0.2 - Math.sin(t * 6 * wave) * 0.45 * (wave > 1 ? 1.3 : 1) : -0.2;
      boArmR.rotation.z = motionOn ? 0.1 + Math.sin(t * 3) * 0.12 : 0.1;
      boShadow.position.set(bo.position.x, GROUND + 0.005 - (1 - boScale) * 0.0, bo.position.z);
      var feetY = baseY - 1.7 * boScale, liftN = clamp((bo.position.y - baseY) / 0.8, 0, 1);
      boShadow.position.y = feetY - 0.02;
      boShadow.scale.set(3.0 * boScale * (1 - 0.35 * liftN), 1.5 * boScale * (1 - 0.35 * liftN), 1);
      boShadow.material.opacity = 0.9 - 0.5 * liftN;

      // ---- decor ----
      decor.forEach(function (m) {
        var u = m.userData;
        m.position.x = u.base.x + smoothP.x * 0.25;
        m.position.y = u.base.y + (motionOn ? Math.sin(t * u.sp + u.ph) * 0.18 : 0);
        if (motionOn) { m.rotation.x += dt * u.rs; m.rotation.y += dt * (u.rs + 0.2); }
      });

      // ---- hover label ----
      if (ui.tip) {
        if (hovered >= 0) {
          var hi = items[hovered], hp = (hi.isBo ? bo.position : hi.root.position).clone().project(camera);
          ui.tip.textContent = hi.name; ui.tip.hidden = false;
          ui.tip.style.left = ((hp.x * 0.5 + 0.5) * W) + 'px'; ui.tip.style.top = ((-hp.y * 0.5 + 0.5) * H - 34) + 'px';
        } else ui.tip.hidden = true;
      }

      placePad();
      renderer.render(scene, camera);
      if (running && (motionOn || busy || press.on || now < onceUntil)) raf = requestAnimationFrame(frame);
      else running = false;
    }
    function start() { if (running || !visible || document.hidden) return; running = true; clock.getDelta(); raf = requestAnimationFrame(frame); }
    function stop() { running = false; cancelAnimationFrame(raf); }
    function kick(ms) { onceUntil = Math.max(onceUntil, performance.now() + (ms || 200)); start(); }

    document.addEventListener('bb:motion', function (e) { motionOn = !!e.detail.on; kick(400); });
    new IntersectionObserver(function (en) { visible = en[0].isIntersecting; if (visible) start(); else { stop(); release(true); } }).observe(stage);
    document.addEventListener('visibilitychange', function () { if (document.hidden) stop(); else start(); });
    if ('ResizeObserver' in window) {
      new ResizeObserver(function () {
        W = stage.clientWidth; H = stage.clientHeight; if (!W || !H) return;
        renderer.setSize(W, H); camera.aspect = W / H; camera.updateProjectionMatrix(); kick(120);
      }).observe(stage);
    }

    // keep base colours rich: reflections are a highlight, not a wash
    scene.traverse(function (o) { var ms = o.material ? (Array.isArray(o.material) ? o.material : [o.material]) : []; ms.forEach(function (m) { if (m && 'envMapIntensity' in m) m.envMapIntensity = 0.35; lin(m); }); });

    // ---------- ready ----------
    stage.classList.add('is-3d');
    if (ui.panel) ui.panel.hidden = false;
    if (ui.pad) ui.pad.hidden = false;
    refreshUI();
    kick(800);

    // Test hook (only with ?bbtest in the URL), used for automated checks.
    if (/[?&]bbtest/.test(location.search)) {
      window.__bbScene = {
        select: select, start: startPress, release: release,
        state: function () { return { active: current().id, pressing: press.on, s: items.map(function (i) { return [i.id, +i.sq.x.toFixed(3)]; }) }; },
        items: items.map(function (i) { return i.id; }),
        // shop checks: where a toy is on screen, and the basket / add state
        toyAt: function (id) {
          var it = items.filter(function (x) { return x.id === id; })[0]; if (!it) return null;
          var p = (it.isBo ? bo.position : it.root.position).clone().project(camera), r = stage.getBoundingClientRect();
          return { x: r.left + (p.x * 0.5 + 0.5) * r.width, y: r.top + (-p.y * 0.5 + 0.5) * r.height };
        },
        toy: function (id) {
          var it = items.filter(function (x) { return x.id === id; })[0]; if (!it) return null;
          var p = it.root ? it.root.position : bo.position;
          return { sq: +it.sq.x.toFixed(3), lag: it.lag ? +it.lag.x.toFixed(3) : null, x: +p.x.toFixed(3), y: +p.y.toFixed(3), z: +p.z.toFixed(3), scale: it.root ? +it.root.scale.x.toFixed(3) : 1, drag: it.dragB ? +it.dragB.x.toFixed(3) : 0, product: !!it.product };
        },
        colors: function (id) {   // average colour of the most / least stretched vertices
          var it = items.filter(function (x) { return x.id === id; })[0], g = it && it.root.children[0] && it.root.children[0].geometry;
          if (!g || !g.attributes.color) return null;
          var c = g.attributes.color.array, lo = [0, 0, 0], hi = [0, 0, 0], nl = 0, nh = 0, P = g.attributes.position.array;
          for (var i = 0; i < c.length; i += 3) { var r2 = Math.hypot(P[i], P[i + 1], P[i + 2]); if (r2 > 1.18) { hi[0] += c[i]; hi[1] += c[i + 1]; hi[2] += c[i + 2]; nh++; } else if (r2 < 1.06) { lo[0] += c[i]; lo[1] += c[i + 1]; lo[2] += c[i + 2]; nl++; } }
          return { stretched: nh ? hi.map(function (v) { return +(v / nh).toFixed(3); }) : null, n: nh, rest: nl ? lo.map(function (v) { return +(v / nl).toFixed(3); }) : null };
        },
        shop: function () { return { busy: shop.busy, count: shop.n, dragging: drag.it ? drag.it.id : null, products: Object.keys(shopData), active: current().id }; },
        setProduct: function (id, p) { items.forEach(function (x) { if (x.id === id) x.product = p; }); if (shop.variant) delete shop.variant.dataset.for; refreshShop(); },
        motion: function () { return motionOn; }
      };
    }
  }

  function init() { document.querySelectorAll('[data-bb-3d]').forEach(boot); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
